export function currencyFractionDigits(currency: string): number {
  const code = currency.trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code })
      .resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
}

export function formatMinorCurrencyInput(
  amountMinor: number | null | undefined,
  currency = 'USD',
): string {
  if (amountMinor == null) return '';
  if (!Number.isSafeInteger(amountMinor)) {
    throw new Error('Currency amount must be a safe integer of minor units.');
  }

  const digits = currencyFractionDigits(currency);
  const scale = 10n ** BigInt(digits);
  const minor = BigInt(amountMinor);
  const sign = minor < 0n ? '-' : '';
  const absolute = minor < 0n ? -minor : minor;
  const whole = absolute / scale;
  if (digits === 0) return `${sign}${whole}`;
  const fraction = String(absolute % scale).padStart(digits, '0');
  return `${sign}${whole}.${fraction}`;
}

export function minorCurrencyToMajorNumber(
  amountMinor: number | null | undefined,
  currency = 'USD',
): number | null {
  if (amountMinor == null) return null;
  return Number(formatMinorCurrencyInput(amountMinor, currency));
}

export function parseCurrencyInputToMinor(
  value: string,
  currency = 'USD',
  label = 'Amount',
): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const match = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))$/.exec(trimmed);
  if (!match) throw new Error(`${label} must be a valid currency amount.`);

  const digits = currencyFractionDigits(currency);
  const fraction = match[3] ?? match[4] ?? '';
  if (fraction.length > digits) {
    throw new Error(`${label} supports no more than ${digits} decimal places for ${currency.toUpperCase()}.`);
  }

  const whole = match[2] || '0';
  const scale = 10n ** BigInt(digits);
  const fractionMinor = digits === 0 ? 0n : BigInt(fraction.padEnd(digits, '0') || '0');
  const sign = match[1] === '-' ? -1n : 1n;
  const amountMinor = sign * (BigInt(whole) * scale + fractionMinor);
  const result = Number(amountMinor);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} is too large.`);
  return result;
}

export function formatMinorCurrency(
  amountMinor: number | null | undefined,
  currency = 'USD',
): string {
  if (amountMinor == null || !Number.isSafeInteger(amountMinor)) return 'Not set';
  const digits = currencyFractionDigits(currency);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.trim().toUpperCase() || 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(formatMinorCurrencyInput(amountMinor, currency)));
}
